import { useReducer } from 'react';

import { useTheme } from 'styled-components';

import { Text, Box } from '@gympass/yoga';
import { useFlag } from '@unleash/proxy-client-react';
import { FormattedMessage } from 'react-intl';
import { getMessageError } from 'utils/helpers';
import { FeatureToggle } from 'utils/unleash/toggles';

import { useModals } from 'context';
import { getEndUserDoc } from 'services/user';

import { Button, LinkBlob, Modal } from 'components';

import DocDownloadAgreement from './DocDownloadAgreement';
import {
  docDownloadInit,
  docDownloadReducer,
  docDownloadState,
} from './docDownloadReducer';

import { DocDownloadProps, docDownloadTypes } from './types';

import * as S from './styles';

export const INFOS = ['1st', '2nd', '3rd'];
const DocDownload: React.FC<DocDownloadProps> = ({
  isOpen,
  modalKey,
  props: { showDischargeInfo, onNext, cognitoId },
}) => {
  const { closeModal } = useModals();
  const isPatientNotesEnabled = useFlag(FeatureToggle.PatientRecords);
  const theme = useTheme() as any; // eslint-disable-line
  const [state, dispatch] = useReducer(
    docDownloadReducer,
    docDownloadState,
    docDownloadInit,
  );

  const infos = isPatientNotesEnabled
    ? INFOS
    : INFOS.filter(info => info !== '3rd');

  function handleClose() {
    closeModal(modalKey);
    dispatch({ type: docDownloadTypes.RESET, payload: docDownloadState });
  }
  async function handleDownloadClick(
    event: React.FormEvent<HTMLButtonElement>,
  ) {
    event.preventDefault();

    if (state.type === docDownloadTypes.SUCCESS) {
      if (onNext) {
        onNext();
      } else {
        handleClose();
      }
    } else {
      dispatch({ type: docDownloadTypes.LOADING });

      try {
        const reports = await getEndUserDoc(cognitoId);

        dispatch({
          type: docDownloadTypes.SUCCESS,
          payload: {
            url: window.URL.createObjectURL(new Blob([reports.data])),
            filename: reports.headers.filename,
          },
        });
      } catch (e: unknown) {
        dispatch({
          type: docDownloadTypes.ERROR,
          payload: {
            message: getMessageError(e, 'DOC_DOWNLOAD.FORM.REQUEST.ERROR'),
          },
        });
      }
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      closeModal={() => handleClose()}
      overlayStyle={{ alignContent: 'center' }}
      contentStyle={{ paddingBottom: `${theme.yoga.spacing.medium}px` }}
    >
      <S.DocDownloadContainer>
        {showDischargeInfo && (
          <S.DischargeInfo>
            <FormattedMessage id="DOC_DOWNLOAD.DISCHARGE.INFO" />
          </S.DischargeInfo>
        )}
        <Text.Small color="deep">
          <FormattedMessage id="DOC_DOWNLOAD.TITLE" />
        </Text.Small>
        <Box mt="4" as="ul">
          {infos.map(info => (
            <S.DocDownloadInfoItem key={info}>
              <S.DocDownloadInfoItemIcon />
              <FormattedMessage id={`DOC_DOWNLOAD.INFO.${info}`} />
            </S.DocDownloadInfoItem>
          ))}
        </Box>
        <form>
          <Box mt="6" mx="0" padding="0" border="none" as="fieldset">
            <Box display="flex" as={Text.Tiny} forwardedAs="legend">
              <S.DocDownloadFormLegendIcon />
              <span>
                <FormattedMessage
                  id="DOC_DOWNLOAD.FORM.LEGEND.TEXT"
                  values={{
                    a: (text: string) => (
                      <S.DocDownloadFormLegendHighlight
                        as="a"
                        target="_blank"
                        rel="noopener noreferrer nofollow"
                        href="https://site.cfp.org.br/wp-content/uploads/2012/07/codigo-de-etica-psicologia.pdf"
                      >
                        {text}
                      </S.DocDownloadFormLegendHighlight>
                    ),
                    b: (text: string) => (
                      <S.DocDownloadFormLegendHighlight>
                        {text}
                      </S.DocDownloadFormLegendHighlight>
                    ),
                  }}
                />
              </span>
            </Box>
            <DocDownloadAgreement
              disabled={state.type !== docDownloadTypes.DEFAULT}
              onAgreement={agreed =>
                dispatch({
                  type: docDownloadTypes.AGREED,
                  payload: {
                    agreed,
                  },
                })
              }
            />
          </Box>
          <Box mt="4" display="flex" justifyContent="center">
            {state.type === docDownloadTypes.DEFAULT && (
              <S.DocDownloadButtonBack onClick={() => handleClose()}>
                <FormattedMessage id="DOC_DOWNLOAD.FORM.BUTTON.BACK" />
              </S.DocDownloadButtonBack>
            )}
            {state.type === docDownloadTypes.SUCCESS && (
              <LinkBlob href={state.url} download={state.filename} />
            )}
            <Button
              icon={
                state.type === docDownloadTypes.SUCCESS && !onNext && state.icon
              }
              loading={state.type === docDownloadTypes.LOADING}
              disabled={!state.agreed}
              onClick={event => handleDownloadClick(event)}
            >
              <FormattedMessage
                id={
                  state.type === docDownloadTypes.SUCCESS && onNext
                    ? 'DOC_DOWNLOAD.FORM.BUTTON.NEXT'
                    : state.button
                }
              />
            </Button>
          </Box>
          {state.type !== docDownloadTypes.DEFAULT && (
            <Box
              as={Text.Tiny}
              textAlign="center"
              fontWeight="medium"
              marginTop="2"
              color={state.color}
            >
              <FormattedMessage id={state.helper} />
            </Box>
          )}
        </form>
      </S.DocDownloadContainer>
    </Modal>
  );
};

export default DocDownload;
